import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import type { Session, User } from '@supabase/supabase-js';

interface AuthState {
  session: Session | null;
  user: User | null;
  isLoading: boolean;
  /** VB-024F — true when the user arrived via a password-reset email link */
  isPasswordRecovery: boolean;
  initialize: () => void;
  signOut: () => Promise<void>;
  /** VB-024F — explicitly exit the recovery shell after a successful password update */
  clearPasswordRecovery: () => void;
}

/** VB-024G — localStorage key persisting recovery mode across page reloads. */
const RECOVERY_MODE_KEY = 'vb-password-recovery';

export const useAuthStore = create<AuthState>((set) => ({
  session: null,
  user: null,
  isLoading: true,
  isPasswordRecovery: false,

  initialize: () => {
    // VB-024F/VB-024G — Synchronous detection: check URL params AND localStorage so that
    // a refresh mid-recovery flow (URL hash already consumed) restores the recovery shell.
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const searchParams = new URLSearchParams(window.location.search);

    // Bug 1: if the hash already carries an auth error (e.g. expired OTP), the recovery
    // flow must NOT activate — clear any stale localStorage flag so the user lands on the
    // normal login form with a human-readable message shown by Auth.tsx.
    if (hashParams.get('error')) {
      localStorage.removeItem(RECOVERY_MODE_KEY);
    }

    const recoveryInUrl =
      hashParams.get('type') === 'recovery' || searchParams.get('type') === 'recovery';
    const recoveryInStorage = localStorage.getItem(RECOVERY_MODE_KEY) === '1';
    let recoveryActivated = recoveryInUrl || recoveryInStorage;
    if (recoveryActivated) {
      if (recoveryInUrl) localStorage.setItem(RECOVERY_MODE_KEY, '1'); // persist on first arrival
      set({ isPasswordRecovery: true });
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      set({ session, user: session?.user ?? null, isLoading: false });
    });

    // VB-024F — On subsequent auth events, keep session + recovery flag in sync.
    supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        recoveryActivated = true;
        localStorage.setItem(RECOVERY_MODE_KEY, '1');
        set({ session, user: session?.user ?? null, isLoading: false, isPasswordRecovery: true });
      } else if (event === 'USER_UPDATED') {
        set({ session, user: session?.user ?? null, isLoading: false });
      } else if (session) {
        if (!recoveryActivated) {
          set({ session, user: session.user, isLoading: false, isPasswordRecovery: false });
        } else {
          set({ session, user: session.user, isLoading: false });
        }
      } else {
        localStorage.removeItem(RECOVERY_MODE_KEY);
        set({ session: null, user: null, isLoading: false, isPasswordRecovery: false });
        recoveryActivated = false;
      }
    });
  },

  signOut: async () => {
    await supabase.auth.signOut();
  },

  // VB-024F — explicit recovery exit after successful password update
  clearPasswordRecovery: () => {
    localStorage.removeItem(RECOVERY_MODE_KEY);
    set({ isPasswordRecovery: false });
  },
}));
